import React, { useState, useEffect } from 'react';
import { Layout as AntLayout, Menu, Dropdown, Avatar, Space, Typography, App as AntdApp } from 'antd';
import type { MenuProps } from 'antd';
import {
  UserOutlined,
  LogoutOutlined,
  FileTextOutlined,
  BankOutlined,
  CoffeeOutlined,
  PlayCircleOutlined,
  ApiOutlined,
  CheckCircleOutlined,
  CalendarOutlined,
  TeamOutlined,
  BarChartOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { authService } from '../services/auth';
import clientStatusService, { ClientStatusSummary, ClientStatusResponse } from '../services/clientStatus';
import { setModalInstance, setMessageInstance } from './PopupModal';
import { useNavigationBlockerContext } from '../contexts/NavigationBlockerContext';

const { Header, Sider, Content } = AntLayout;
const { Title } = Typography;

const Layout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedTopMenu, setSelectedTopMenu] = useState<string>('content-management');
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const [clientStatus, setClientStatus] = useState<ClientStatusSummary>({ total: 0, online: 0, offline: 0 });
  const { checkAndConfirmNavigation } = useNavigationBlockerContext();

  // Modal & Message instance 초기화
  const { modal, message } = AntdApp.useApp();

  useEffect(() => {
    setModalInstance(modal);
    setMessageInstance(message);
  }, [modal, message]);

  const user = authService.getCurrentUser();
  const isSuperAdmin = user?.role === 'super_admin' || user?.role === 'development_admin';

  // 상단 메뉴 아이템들 (권한별 필터링)
  const topMenuItems: MenuProps['items'] = isSuperAdmin
    ? [
      {
        key: 'content-management',
        icon: <FileTextOutlined />,
        label: '콘텐츠 관리',
      },
      {
        key: 'schedule-management',
        icon: <CalendarOutlined />,
        label: '스케쥴 관리',
      },
      {
        key: 'permission-management',
        icon: <TeamOutlined />,
        label: '권한 관리',
      },
      {
        key: 'reports',
        icon: <BarChartOutlined />,
        label: '리포트',
      },
    ]
    : [
      {
        key: 'content-management',
        icon: <FileTextOutlined />,
        label: '콘텐츠 관리',
      },
    ];

  // 선택된 상단 메뉴에 따른 사이드 메뉴 (권한별 필터링)
  const getSideMenuItems = (topMenuKey: string): MenuProps['items'] => {
    switch (topMenuKey) {
      case 'content-management':
        // 수퍼 관리자: 모든 메뉴 표시
        if (isSuperAdmin) {
          return [
            {
              key: 'building-info',
              icon: <BankOutlined />,
              label: '건물 안내',
              children: [
                {
                  key: '/tenants',
                  label: '입주사 관리',
                },
                {
                  key: '/tenant-intro',
                  label: '입주사 소개',
                },
                {
                  key: '/notices',
                  label: '공지사항',
                },
              ],
            },
            {
              key: 'life-culture',
              icon: <CoffeeOutlined />,
              label: '라이프&컬쳐',
              children: [
                {
                  key: '/restaurants',
                  label: '맛집 가이드',
                },
                {
                  key: '/popups',
                  label: '팝업 가이드',
                },
              ],
            },
            {
              key: 'media-art',
              icon: <PlayCircleOutlined />,
              label: '미디어 아트',
              children: [
                {
                  key: '/media-management',
                  label: '미디어 관리',
                },
                {
                  key: '/event-management',
                  label: '이벤트 관리',
                },
                {
                  key: '/interaction-management',
                  label: '인터랙션 관리',
                },
                {
                  key: '/greeting-management',
                  label: '그리팅',
                },
              ],
            },
            {
              key: '/api-management',
              icon: <ApiOutlined />,
              label: 'API 현황',
            },
            {
              key: '/content-approval',
              icon: <CheckCircleOutlined />,
              label: '콘텐츠 승인 관리',
            },
          ];
        }
        // 일반 관리자: 제한된 메뉴만 표시
        return [
          {
            key: 'building-info',
            icon: <BankOutlined />,
            label: '건물 안내',
            children: [
              {
                key: '/tenant-intro',
                label: '입주사 소개',
              },
              {
                key: '/notices',
                label: '공지사항',
              },
            ],
          },
          {
            key: 'media-art',
            icon: <PlayCircleOutlined />,
            label: '미디어 아트',
            children: [
              {
                key: '/greeting-management',
                label: '그리팅',
              },
            ],
          },
        ];
      case 'schedule-management':
        return [
          {
            key: '/schedule-management',
            icon: <CalendarOutlined />,
            label: '스케쥴 관리',
          },
        ];
      case 'permission-management':
        return [
          {
            key: '/permission-management',
            icon: <TeamOutlined />,
            label: '권한 관리',
          },
        ];
      case 'reports':
        return [
          {
            key: '/reports',
            icon: <BarChartOutlined />,
            label: '리포트',
          },
        ];
      default:
        return [];
    }
  };

  // 현재 경로에 따라 열려야 할 메뉴 키들을 반환
  const getOpenKeysFromPath = (path: string): string[] => {
    if (path.includes('/tenants') || path.includes('/tenant-intro') || path.includes('/notices')) {
      return ['building-info'];
    } else if (path.includes('/restaurants') || path.includes('/popups')) {
      return ['life-culture'];
    } else if (path.includes('/media-management') || path.includes('/event-management') ||
      path.includes('/interaction-management') ||
      path.includes('/greeting-management') || path.includes('/greeting-detail') ||
      path.includes('/greeting-add') || path.includes('/greeting-edit')) {
      return ['media-art'];
    }
    return [];
  };

  // 현재 경로에서 선택된 메뉴 키를 반환
  const getSelectedKeyFromPath = (path: string): string => {
    if (path.includes('/tenants')) return '/tenants';
    if (path.includes('/tenant-intro')) return '/tenant-intro';
    if (path.includes('/notices')) return '/notices';
    if (path.includes('/restaurants')) return '/restaurants';
    if (path.includes('/popups')) return '/popups';
    if (path.includes('/media-management')) return '/media-management';
    if (path.includes('/event-management')) return '/event-management';
    if (path.includes('/interaction-management')) return '/interaction-management';
    if (path.includes('/greeting-management')) return '/greeting-management';
    if (path.includes('/api-management')) return '/api-management';
    if (path.includes('/content-approval')) return '/content-approval';
    if (path.includes('/schedule-management')) return '/schedule-management';
    if (path.includes('/permission-management')) return '/permission-management';
    if (path.includes('/reports')) return '/reports';
    return path;
  };

  // 사용자 드롭다운 메뉴
  const userMenuItems: MenuProps['items'] = [
    // {
    //   key: 'profile',
    //   icon: <UserOutlined />,
    //   label: '프로필 설정',
    // },
    // {
    //   type: 'divider',
    // },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '로그아웃',
      danger: true,
    },
  ];

  const handleTopMenuClick = ({ key }: { key: string }) => {
    const targetPath =
      key === 'content-management' ? (isSuperAdmin ? '/tenants' : '/tenant-intro') :
      key === 'schedule-management' ? '/schedule-management' :
      key === 'permission-management' ? '/permission-management' :
      key === 'reports' ? '/reports' : null;

    if (targetPath) {
      checkAndConfirmNavigation(targetPath, () => {
        setSelectedTopMenu(key);
        navigate(targetPath);
      });
    }
  };

  const handleSideMenuClick = ({ key }: { key: string }) => {
    if (key.startsWith('/')) {
      checkAndConfirmNavigation(key, () => {
        navigate(key);
      });
    }
  };

  const handleOpenChange = (keys: string[]) => {
    setOpenKeys(keys);
  };

  const handleUserMenuClick = async ({ key }: { key: string }) => {
    switch (key) {
      case 'profile':
        navigate('/profile');
        break;
      case 'logout':
        try {
          await authService.logout();
          navigate('/login');
        } catch (error) {
          console.error('Logout error:', error);
          navigate('/login');
        }
        break;
    }
  };

  // 클라이언트 상태 업데이트 함수 (수동 새로고침용)
  const updateClientStatus = async () => {
    try {
      const status = await clientStatusService.getClientStatus();
      setClientStatus(status.summary);
    } catch (error) {
      console.error('Failed to update client status:', error);
    }
  };

  // SSE 실시간 상태 업데이트 핸들러
  const handleSSEStatusUpdate = (status: ClientStatusResponse) => {
    setClientStatus(status.summary);
  };

  // 현재 경로에 따라 선택된 상단 메뉴 및 열린 메뉴 결정
  useEffect(() => {
    const path = location.pathname;

    // 상단 메뉴 설정
    if (path.includes('/tenants') || path.includes('/tenant-intro') || path.includes('/notices') ||
      path.includes('/restaurants') || path.includes('/popups') || path.includes('/media-management') ||
      path.includes('/event-management') || path.includes('/interaction-management') ||
      path.includes('/greeting-management') ||
      path.includes('/greeting-detail') || path.includes('/greeting-add') || path.includes('/greeting-edit') ||
      path.includes('/api-management') || path.includes('/content-approval')) {
      setSelectedTopMenu('content-management');
    } else if (path.includes('/schedule-management')) {
      setSelectedTopMenu('schedule-management');
    } else if (path.includes('/permission-management')) {
      setSelectedTopMenu('permission-management');
    } else if (path.includes('/reports')) {
      setSelectedTopMenu('reports');
    }

    // 열린 메뉴 설정
    setOpenKeys(getOpenKeysFromPath(path));
  }, [location.pathname]);

  // SSE 연결 및 클라이언트 상태 관리
  useEffect(() => {
    // 초기 로드
    updateClientStatus();

    // SSE 연결 시작
    clientStatusService.startSSEConnection();
    clientStatusService.addStatusChangeListener(handleSSEStatusUpdate);

    // 백업용 폴링 (2분마다 - SSE 실패 시 대비)
    const fallbackInterval = setInterval(updateClientStatus, 120000);

    return () => {
      // 정리
      clearInterval(fallbackInterval);
      clientStatusService.removeStatusChangeListener(handleSSEStatusUpdate);
      clientStatusService.stopSSEConnection();
    };
  }, []);

  return (
    <AntLayout style={{ minHeight: '100vh' }}>
      {/* 상단 헤더 with 메뉴바 */}
      <Header
        style={{
          background: '#1f1f1f',
          paddingLeft: '0px',
          paddingRight: '24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #303030',
          height: '64px',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0px' }}>
          <Title
            level={4}
            style={{
              color: '#ffffff',
              margin: 0,
              whiteSpace: 'nowrap',
              width: '280px',
              alignItems: 'center',
              justifyContent: 'center',
              display: 'flex',
              backgroundColor: '#000000',
              height: '64px',
              // 우측에 1px 라인 보더 추가
              borderRight: '1px solid #303030',
              cursor: 'pointer',
            }}
            onClick={() => {
              const defaultPath = isSuperAdmin ? '/tenants' : '/tenant-intro';
              checkAndConfirmNavigation(defaultPath, () => {
                setSelectedTopMenu('content-management');
                navigate(defaultPath);
              });
            }}
          >
            <span style={{ height: '32px', display: 'flex', alignItems: 'flex-end', gap: 8, fontSize: '12px', color: '#FFFFFFFF', fontWeight: '600' }}>
              <img
                src="/assets/logo_brick.svg"
                alt="Brick Logo"
                style={{ height: '32px', marginRight: 0, verticalAlign: 'middle' }}
              />
            </span>
          </Title>
          <Menu
            mode="horizontal"
            theme="dark"
            selectedKeys={[selectedTopMenu]}
            items={topMenuItems}
            onClick={handleTopMenuClick}
            style={{
              background: 'transparent',
              border: 'none',
              flex: 1,
              minWidth: isSuperAdmin ? 0 : 121,
            }}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {/* 클라이언트 연결 상태 */}
          <Space
            style={{
              cursor: 'pointer'
            }}
            onClick={updateClientStatus}
            title="클릭하여 상태 새로고침"
          >
            <span style={{ color: '#ffffff', fontSize: '13px' }}>
              {clientStatusService.getStatusText(clientStatus)}
            </span>
            <span
              style={{
                color: clientStatusService.getStatusColor(clientStatus),
                fontSize: '14px',
                fontWeight: 'bold'
              }}
            >
              {clientStatusService.getStatusIcon(clientStatus)}
            </span>
          </Space>

          <Dropdown
            menu={{
              items: userMenuItems,
              onClick: handleUserMenuClick,
            }}
            placement="bottomRight"
          >
            <Space style={{ cursor: 'pointer', color: '#ffffff' }}>
              <Avatar size="small" icon={<UserOutlined />} style={{ backgroundColor: '#F24822' }} />
              <span style={{ color: '#ffffff' }}>{user?.name || user?.email}</span>
              <span style={{ color: '#a6a6a6', fontSize: '12px' }}>
                ({user?.role === 'development_admin' ? 'Development Admin' : user?.role === 'super_admin' ? '슈퍼 관리자' : '관리자'})
              </span>
            </Space>
          </Dropdown>
        </div>
      </Header>

      <AntLayout style={{ marginTop: '64px' }}>
        {/* 좌측 사이드바 */}
        <Sider
          theme="dark"
          width={280}
          style={{
            background: '#000000',
            borderRight: '1px solid #303030',
            position: 'fixed',
            left: 0,
            top: '64px',
            bottom: 0,
            zIndex: 999,
            height: 'calc(100vh - 64px)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div
            style={{
              flex: 1,
              overflowY: 'auto',
              overflowX: 'hidden',
            }}
          >
            <Menu
              mode="inline"
              theme="dark"
              selectedKeys={[getSelectedKeyFromPath(location.pathname)]}
              openKeys={openKeys}
              onOpenChange={handleOpenChange}
              items={getSideMenuItems(selectedTopMenu)}
              onClick={handleSideMenuClick}
              style={{
                background: '#000000',
                border: 'none',
                borderRadius: '0px',
              }}
            />
          </div>

          {/* Copyright 섹션 */}
          <div
            style={{
              position: 'absolute',
              bottom: 0,
              left: '50%',
              width: '100%',
              transform: 'translateX(-50%)',
              padding: '24px 16px',
              borderTop: '1px solid #303030',
              background: '#000000',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '12px',
            }}
          >
            {/* <img
              src="/assets/logo_brick.svg"
              alt="H9 Interaction Logo"
              style={{
                height: '10px',
                opacity: 0.8,
              }}
            /> */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '0px',
              }}
            >
              <span
                style={{
                  color: '#666666',
                  fontSize: '11px',
                  fontWeight: '500',
                  letterSpacing: '0.5px',
                }}
              >
                © 2025 H9 INTERACTION
                <span
                  style={{
                    color: '#4a4a4a',
                    fontSize: '10px',
                    letterSpacing: '0.3px',
                    marginLeft: '4px',
                  }}
                >
                  All rights reserved.
                </span>
              </span>
              <span
                style={{
                  color: '#4a4a4a',
                  fontSize: '10px',
                  letterSpacing: '0.3px',
                  // marginLeft: '4px',
                }}
              >
                version 0.0.1
              </span>
            </div>
          </div>
        </Sider>

        {/* 메인 콘텐츠 */}
        <Content
          style={{
            margin: '24px',
            marginLeft: '304px', // 280px(사이드바) + 24px(여백)
            padding: '24px',
            background: '#141414',
            borderRadius: '0px',
            minHeight: 'calc(100vh - 112px)', // 64px(헤더) + 48px(상하 여백)
          }}
        >
          <Outlet />
        </Content>
      </AntLayout>
    </AntLayout>
  );
};

export default Layout;